import { useCallback } from "react"

import {
  DEMAND2_PERF_WINDOWS,
  type Demand2WindowStats,
  useDemand2Performance,
} from "~/components/demand-2/shared/useDemand2Performance"
import { performanceStatus } from "~/components/demand-2/tabs/performanceStatus"
import type { IVariantSummary } from "~/hooks/useVariantTableData"

import {
  PERFORMANCE_STATUS_FIELD,
  PERFORMANCE_STATUS_WEEKS,
  salesVsExpectedFields,
} from "./ruleFields"

export const PERFORMANCE_RULE_FIELDS: readonly string[] = [
  PERFORMANCE_STATUS_FIELD,
  ...DEMAND2_PERF_WINDOWS.flatMap((weeks) => {
    const { signed, abs, units, absUnits } = salesVsExpectedFields(weeks)
    return [signed, abs, units, absUnits]
  }),
]

/** Sales vs published expected demand per window; only published variants have an expected side. */
function performanceRuleValues(
  variantId: string,
  byWindow: ReadonlyMap<number, Demand2WindowStats>,
  inPublish: boolean,
): Record<string, unknown> {
  const values: Record<string, unknown> = {}
  for (const weeks of DEMAND2_PERF_WINDOWS) {
    const stats = byWindow.get(weeks)?.statsByVariant.get(variantId)
    const expected = stats?.totalExpectedDemand ?? 0
    const sales = stats?.totalSales ?? 0
    const pct = inPublish && expected > 0 ? ((sales - expected) / expected) * 100 : undefined
    const fields = salesVsExpectedFields(weeks)
    values[fields.signed] = pct
    values[fields.abs] = pct === undefined ? undefined : Math.abs(pct)
    values[fields.units] = inPublish ? sales - expected : undefined
    values[fields.absUnits] = inPublish ? Math.abs(sales - expected) : undefined
    values[fields.sales] = inPublish ? sales : undefined
    values[fields.expected] = inPublish ? expected : undefined
    if (weeks === PERFORMANCE_STATUS_WEEKS) {
      values[PERFORMANCE_STATUS_FIELD] = performanceStatus({
        anyPublished: inPublish,
        expectedNA: false,
        expected,
        sales,
      })
    }
  }
  return values
}

/**
 * Stamps the performance rule fields onto variants, from the same per-variant numbers the Performance
 * tab shows. Fetches and copies rows only while `ruleFields` (see `useRuleFieldsWhenShown`) needs them;
 * otherwise it hands the variants back untouched.
 */
export function usePerformanceRuleStamp({
  collectionIds,
  variantIds,
  ruleFields,
  isPublished,
}: {
  collectionIds: number[]
  variantIds: string[]
  ruleFields: ReadonlySet<string>
  isPublished: (variantId: string) => boolean
}) {
  const needed = PERFORMANCE_RULE_FIELDS.some((f) => ruleFields.has(f))
  const { byWindow } = useDemand2Performance({
    collectionIds,
    displayVariantIds: variantIds,
    windows: DEMAND2_PERF_WINDOWS,
    enabled: needed,
  })
  return useCallback(
    <V extends IVariantSummary>(variants: V[]): V[] =>
      needed
        ? variants.map((v) => ({
            ...v,
            ...performanceRuleValues(v.variantId, byWindow, isPublished(v.variantId)),
          }))
        : variants,
    [needed, byWindow, isPublished],
  )
}
